/**
 * Reports Hooks
 * React Query hooks for reports dashboard data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi, type CostConfig } from '@/lib/api/reports';

// Query keys
const REPORTS_KEYS = {
  all: ['reports'] as const,
  dashboard: (startDate?: string, endDate?: string) =>
    [...REPORTS_KEYS.all, 'dashboard', { startDate, endDate }] as const,
  users: (startDate?: string, endDate?: string) =>
    [...REPORTS_KEYS.all, 'users', { startDate, endDate }] as const,
  daily: (startDate?: string, endDate?: string) =>
    [...REPORTS_KEYS.all, 'daily', { startDate, endDate }] as const,
  config: () => [...REPORTS_KEYS.all, 'config'] as const,
};

export interface UseReportsDashboardOptions {
  startDate?: string;
  endDate?: string;
  refetchInterval?: number;
  enabled?: boolean;
}

/**
 * Hook for fetching complete reports dashboard
 */
export function useReportsDashboard(options: UseReportsDashboardOptions = {}) {
  const { startDate, endDate, refetchInterval, enabled = true } = options;

  return useQuery({
    queryKey: REPORTS_KEYS.dashboard(startDate, endDate),
    queryFn: () => reportsApi.getDashboard(startDate, endDate),
    refetchInterval,
    enabled,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for fetching user statistics
 */
export function useUserStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: REPORTS_KEYS.users(startDate, endDate),
    queryFn: () => reportsApi.getUserStats(startDate, endDate),
    staleTime: 30000,
  });
}

/**
 * Hook for fetching daily statistics
 */
export function useDailyStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: REPORTS_KEYS.daily(startDate, endDate),
    queryFn: () => reportsApi.getDailyStats(startDate, endDate),
    staleTime: 30000,
  });
}

/**
 * Hook for fetching cost configuration
 */
export function useCostConfig() {
  return useQuery({
    queryKey: REPORTS_KEYS.config(),
    queryFn: reportsApi.getCostConfig,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook for updating cost configuration
 */
export function useUpdateCostConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: CostConfig) => reportsApi.updateCostConfig(config),
    onSuccess: () => {
      // Invalidate all reports queries to refetch with new config
      queryClient.invalidateQueries({ queryKey: REPORTS_KEYS.all });
    },
  });
}

// Re-export types
export type { ReportsDashboard, CostConfig } from '@/lib/api/reports';
